/**
 * networkSearchConfig.js
 *
 * Portable configuration functions for the Network Search page.
 * Each function takes a clientCode (string) and returns content data or null.
 * Source: ASHLink2008/public/Applications/NetworkSearch/NetworkSearch.aspx(.vb)
 *
 * All clientCode comparisons expect the value to already be lowercased by the caller.
 */

// ---------------------------------------------------------------------------
// Header (line 95)
// ---------------------------------------------------------------------------

/**
 * @param {string} clientCode
 * @returns {string}
 */
export const getHeaderText = clientCode => {
	switch (clientCode) {
		case 'acumercppo':
		case 'optimappo':
			return 'Find A Provider - Optima Health PPO';
		case 'alignment':
			return 'Find a Chiropractor or Acupuncturist';
		case 'optimahmo':
			return 'Find A Provider - Optima Health HMO';
		case 'optimamedi':
			return 'Find A Provider - Optima Health Medicare';
		case 'optimapos':
			return 'Find A Provider - Optima Health POS';
		case 'kpcamedi':
		case 'kphimedi':
			return 'Find A Provider - Medicare';
		default:
			return 'Find A Provider';
	}
};

// ---------------------------------------------------------------------------
// Plan eligibility notice — ABOVE the iframe (line 96)
// Only "alignment" appears here. HMSA and phealth are BELOW the iframe.
// ---------------------------------------------------------------------------

/**
 * @param {string} clientCode
 * @returns {{ heading: string|null, body: string } | null}
 */
export const getPlanEligibilityNotice = clientCode => {
	switch (clientCode) {
		case 'alignment':
			return {
				heading:
					'For Alignment Health Plan members with qualified plans.',
				body: 'Benefits vary by plan. To check if you are eligible for acupuncture or chiropractor services, please refer to your Evidence of Coverage for a full description of benefits.',
			};
		default:
			return null;
	}
};

// ---------------------------------------------------------------------------
// Plan navigation notice — ABOVE the iframe (lines 102–147)
// Directs members to alternate searches by membership type.
// ---------------------------------------------------------------------------

/**
 * @param {string} clientCode
 * @returns {{ items: Array<{ label: string|null, text: string, href: string|null, linkText: string|null }> } | null}
 */
export const getPlanNavigationNotice = clientCode => {
	switch (clientCode) {
		case 'bsc':
			return {
				items: [
					{
						label: 'Medicare Members',
						text: 'If you are a Medicare member, please visit the Medicare Provider Search.',
						href: '/networksearch/BSCAMA',
						linkText: 'Medicare Provider Search',
					},
				],
			};
		case 'bscama':
		case 'bscaphp':
			return {
				items: [
					{
						label: null,
						text: 'To find a provider participating in the Acupuncture Chronic Low Back Pain Program, click here.',
						href: '/ASH/BSCAMACLBP',
						linkText: 'here',
					},
				],
			};
		case 'kaiserca':
			return {
				items: [
					{
						label: 'Commercial Members',
						text: 'If you are a Commercial member, please use the search below to find a participating provider.',
						href: null,
						linkText: null,
					},
					{
						label: 'Medicare Members',
						text: 'If you are a Medicare member, please visit the Medicare Provider Search.',
						href: '/ASH/kaisercamedicare',
						linkText: 'Medicare Provider Search',
					},
					{
						label: 'Medi-Cal Members',
						text: 'If you are a Medi-Cal member, please visit the Medi-Cal Provider Search.',
						href: '/networksearch/KP_MediCal',
						linkText: 'Medi-Cal Provider Search',
					},
					{
						label: 'Exclusive Provider Organization (EPO) Members',
						text: 'If you are an EPO member, please visit the EPO Provider Search.',
						href: '/networksearch/KPCA_EPO',
						linkText: 'EPO Provider Search',
					},
				],
			};
		case 'kaiserhic':
			return {
				items: [
					{
						label: 'Medicare Members',
						text: 'If you are a Medicare member, please visit the Medicare Provider Search.',
						href: '/ASH/kaiserhimedicare',
						linkText: 'Medicare Provider Search',
					},
				],
			};
		case 'ihp':
			return {
				items: [
					{
						label: 'Commercial Members',
						text: 'If you are a Commercial member, please visit the Participating Provider Search.',
						href: '/networksearch/IHPC',
						linkText: 'Participating Provider Search',
					},
					{
						label: 'Medicare Members',
						text: 'If you are a Medicare member, please visit the Medicare Provider Search.',
						href: '/networksearch/IHPM',
						linkText: 'Medicare Provider Search',
					},
				],
			};
		default:
			return null;
	}
};

// ---------------------------------------------------------------------------
// Disclaimer — BELOW the iframe (lines 155–220)
// Kaiser, Sharp, BrandNewDay, LaCare.
//
// NOTE: PanelSharpRefferal has language access content embedded directly
// inside it in the ASPX (lines 179–207). It is NOT driven by getLanguageAccessContent.
// ---------------------------------------------------------------------------

/**
 * @param {string} clientCode
 * @returns {{ text: string, hasEmbeddedLanguageAccess: boolean } | null}
 */
export const getDisclaimerContent = clientCode => {
	switch (clientCode) {
		case 'kaiserca':
		case 'kpcamedi':
			return {
				text: 'This is not a guarantee of coverage. Please check with your Benefits Coordinator, Evidence of Coverage or Kaiser Permanente Member Services to determine whether you have covered benefits provided through ASH. A referral may be required.',
				hasEmbeddedLanguageAccess: false,
			};
		case 'brndnwday':
			return {
				text: 'A referral is required to access your chiropractic and acupuncture benefit. For more information on how to obtain a referral, please contact your health plan.',
				hasEmbeddedLanguageAccess: false,
			};
		case 'lacareca':
		case 'lacarechi':
			return {
				text: 'A referral is not required to access your benefit. Medical necessity verification is required as applicable and submitted to ASH by your practitioner.',
				hasEmbeddedLanguageAccess: false,
			};
		case 'sharphp':
			return {
				text: 'Information in this directory is updated daily; however the provider network may change at any time. You will receive notice when necessary. If information in this directory appears to be inaccurate, you may email ProviderInfo@ashn.com, complete an online form, or call provider services at 1-800-972-4226 option 4.',
				hasEmbeddedLanguageAccess: true, // language access block is inside this panel in the ASPX
			};
		default:
			return null;
	}
};

// ---------------------------------------------------------------------------
// Attribution notice — BELOW the iframe (line 221)
// Only HMSA. Was incorrectly placed above the iframe in the previous version.
// ---------------------------------------------------------------------------

/**
 * @param {string} clientCode
 * @returns {string | null}
 */
export const getAttributionNotice = clientCode => {
	if (clientCode === 'hmsa') {
		return 'American Specialty Health (ASH) is an independent company providing chiropractic, acupuncture, and/or massage therapy services on behalf of HMSA.';
	}
	return null;
};

// ---------------------------------------------------------------------------
// Directory accuracy block — BELOW the iframe (line 226)
// Hidden for phealth, priority2, sharphp.
// Language access content is nested INSIDE this block in the ASPX —
// it only renders when this block renders.
// ---------------------------------------------------------------------------

/**
 * @param {string} clientCode
 * @returns {boolean}
 */
export const showDirectoryAccuracyBlock = clientCode => {
	const hidden = ['phealth', 'priority2', 'sharphp'];
	return !hidden.includes(clientCode);
};

export const DIRECTORY_ACCURACY_CONTENT = {
	accuracyEmail: 'ProviderInfo@ashn.com',
	accuracyFormHref:
		'/ASH/public/Applications/ProviderSearch/InaccuracyReportingForm.aspx',
	accuracy:
		'Information in this directory is updated daily; however the provider network may change at any time. You will receive notice when necessary. If information in this directory appears to be inaccurate, you may email ProviderInfo@ashn.com, complete an online form, or call provider services at 1-800-972-4226 option 4.',
	ada: 'Enrollees are entitled to full and equal access to covered services, including enrollees with disabilities as required under the federal Americans with Disabilities Act of 1990 and Section 504 of the Rehabilitation Act of 1973.',
	openAccess:
		'ASH operates an \u201Copen access\u201D model in which any qualified provider/practitioner can submit an application and signed agreement to be considered for network participation. For more information on ASH\u2019s open network, visit ASH\u2019s recruitment pages on ASHLink.com.',
};

// ---------------------------------------------------------------------------
// Language access — nested INSIDE PanelMemberInfo (lines 243–303)
// Only renders when showDirectoryAccuracyBlock() is true.
// sharphp is excluded here — its language content is embedded in the disclaimer.
// ---------------------------------------------------------------------------

/**
 * @param {string} clientCode
 * @returns {{ type: "standard" | "fallon" | "calviva" | "blueshield" | "chw" } | null}
 */
export const getLanguageAccessContent = clientCode => {
	switch (clientCode) {
		case 'fhp':
		case 'fchp':
			return { type: 'fallon' };
		case 'calviva':
			return { type: 'calviva' };
		case 'bsc':
		case 'bscama':
		case 'bsofcaaff':
		case 'bsofcachi':
		case 'bsofcaacu':
		case 'bsca':
		case 'bscamaclbp':
			return { type: 'blueshield' };
		case 'chw':
			return { type: 'chw' };
		// sharphp: language access is embedded inside the disclaimer, not here
		// phealth, priority2: PanelMemberInfo is hidden so this never renders anyway
		case 'sharphp':
		case 'phealth':
		case 'priority2':
		case 'bscaphp':
		case 'ivhpchiacu':
		case 'ivhpsupchi':
			return null;
		default:
			return { type: 'standard' };
	}
};

export const LANGUAGE_ACCESS_CONTENT = {
	standard: {
		phone: '1-800-678-9133',
		hours: 'Monday through Friday 5 am to 6 pm Pacific Time',
		languages: [
			{
				lang: 'en',
				language: 'English',
				label: 'Important',
				text: 'If you have language needs other than English, we can have somebody help you. You may call 1-800-678-9133 Monday through Friday 5 am to 6 pm Pacific Time. There is no fee for this service.',
			},
			{
				lang: 'es',
				language: 'Spanish',
				label: 'Importante',
				text: 'Si le resulta difícil leer este documento y necesita ayuda en otro idioma que no es el inglés, tenemos a alguien que le puede ayudar. Puede llamar al 1-800-678-9133 de lunes a viernes, de 5 a. m. a 6 p. m. hora del Pacífico. No hay cargo por ese servicio.',
			},
			{
				lang: 'zh',
				language: 'Traditional Chinese',
				label: '請注意',
				text: `如果您看不懂本文件，并需要除英語以外的其他語言援助，我們可以幫助您。您可以在週一至週五太平洋時間早上5點到下午6點致電 1-800-678-9133。本服務免費。`,
			},
			{
				lang: 'tl',
				language: 'Tagalog',
				label: 'Importante',
				text: 'Kung nahihirapan kang basahin ang dokumentong ito at mangangailangan ka ng tulong sa wika na bukod sa Ingles, maaari kaming magbigay ng tulong sa iyo. Maaari kang tumawag sa 1-800-678-9133 mula Lunes hanggang Biyernes ika-lima ng umaga hanggang ika-anim ng hapon, oras sa Pasipiko. Ang serbisyong ito ay walang bayad.',
			},
			{
				lang: 'nv',
				language: 'Navajo',
				label: `Díí T'áá Íiyisíí Ílí̖`,
				text: `Díí naaltsoos yídííłtah ná nanitł'ago áádóó Bilagáana bizaad doo dinits'a'dago ła'da níká adoolwol'ígíí nohee hóló. Béésh bee hodíílnih 1-800-678-9133 jį. Damóo biiskání dóó nida'iiní', abínígo tseebíidi dóó e'e'áahgo ashdladi Ahééháshį́į́hjí oolkilígíí hoodíílnih. Doo bá̖á̖hílída áajii̖' hojilne'go.`,
			},
		],
	},
	fallon: {
		notes: [
			'For Customer Service, please call the phone number on the back of your member ID card.',
			'For Fallon Medicare Plus\u2122 and NaviCare\u00AE members, our hours are 8 a.m.\u20138 p.m. EST, Monday\u2013Friday. (Oct. 1\u2013March 31, seven days a week.)',
			'For MassHealth members, our hours are Monday\u2013Friday, 8 a.m.\u20136 p.m EST.',
			'For everyone else, our hours are Monday, Tuesday, Thursday and Friday from 8 a.m.\u20136 p.m EST. and Wednesday from 10 a.m.\u2013 6 p.m EST.',
		],
	},
	calviva: {
		links: [
			{
				text: 'Notice of Language Assistance Services',
				href: '/ASH/WCMGenerated/CalVivaNOLA_tcm17-177528.pdf',
			},
			{
				text: 'CalViva NonDiscrimination Notice',
				href: '/ASH/WCMGenerated/CalVivaNonDiscrimination_tcm17-177529.pdf',
			},
		],
	},
	blueshield: {
		heading: 'Language Access Services',
		subtext:
			'Language Assistance Services include certified Qualified Medical Interpreters (QMI)',
		imageUrl: '/ASH/public/images/LangForBlueShieldProviders.jpg',
		nondiscriminationLink: {
			text: 'Nondiscrimination Notice',
			href: '/ASH/public/Applications/ProviderSearch/pdfs/BlueShieldCA_NonDiscriminateNotice.pdf',
		},
	},
	chw: {
		links: [
			{
				text: 'Language Assistance',
				href: '/ASH/public/Applications/ProviderSearch/pdfs/cahealthwellness.pdf',
			},
		],
	},
};

// ---------------------------------------------------------------------------
// Priority Health member actions — BELOW the iframe (lines 312–336)
// Replaces the directory accuracy block for priority2.
// ---------------------------------------------------------------------------

/**
 * @param {string} clientCode
 * @returns {{ items: Array<{ text: string, href: string|null, linkText: string|null }> } | null}
 */
export const getPriorityHealthMemberActions = clientCode => {
	if (clientCode !== 'priority2') return null;
	return {
		items: [
			{
				text: 'If you do not see a provider in your area or you have a provider you would like to see as part of our network, let us know! Call us at (800) 678-9133 or visit the Nominate a Provider Page. ASH will work with you to ensure that you get coverage from a qualified provider near you.',
				href: '/ASH/public/Applications/ProviderSearch/Nominate.aspx',
				linkText: 'Nominate a Provider Page',
			},
			{
				text: 'Information in this directory is updated daily and can change without notice. Remember to confirm with your provider that they are a part of the ASH Group network before receiving treatment and verify what your financial responsibility will include. Call Member Services at (800) 678-9133 for more information.',
				href: null,
				linkText: null,
			},
			{
				text: 'Learn more about acupuncture HERE.',
				href: '/ASH/WCMGenerated/4810_ACU.pdf',
				linkText: 'HERE',
			},
			{
				text: 'Learn more about medical massage HERE.',
				href: '/ASH/WCMGenerated/4810_MT.pdf',
				linkText: 'HERE',
			},
			{
				text: 'To file a grievance, please use the online form or print and return the form to ASH. Both the electronic and PDF forms can be found HERE.',
				href: '/ASH/public/Applications/Members/Grievance.aspx?MemType=3',
				linkText: 'HERE',
			},
		],
	};
};

// ---------------------------------------------------------------------------
// Priority Health eligibility notice — BELOW the iframe (lines 305–311)
// phealth only. Was incorrectly placed above the iframe in the previous version.
// ---------------------------------------------------------------------------

/**
 * @param {string} clientCode
 * @returns {string | null}
 */
export const getPriorityHealthEligibilityNotice = clientCode => {
	if (clientCode !== 'phealth') return null;
	return 'This directory is for use by Priority Health members enrolled in a plan that includes coverage for medically-necessary acupuncture and medical massage services. If you have questions about your Priority Health Plan, call the number on the back of your membership card.';
};

// ---------------------------------------------------------------------------
// CA Timely Access — always shown, no client logic (lines 337–351)
// ---------------------------------------------------------------------------

export const CA_TIMELY_ACCESS_CONTENT = {
	heading: 'For California members - Timely Access to Care',
	intro: 'ASH has established mechanisms to ensure the accessibility of services in a timely manner appropriate to the member\u2019s condition consistent with good professional practice. ASH provides and maintains standards for access to:',
	standards: [
		'Regular and routine care appointments and rescheduling thereof: within seven (7) calendar days',
		'Urgent care and rescheduling thereof: within 24 hours (as applicable)',
	],
};

// ---------------------------------------------------------------------------
// Footer language link grid (footer section)
// ---------------------------------------------------------------------------

/**
 * @param {string} clientCode
 * @returns {boolean}
 */
export const showFooterLanguageLinks = clientCode => {
	const hidden = [
		'bsc',
		'bscama',
		'bsofcaaff',
		'bsofcachi',
		'bsofcaacu',
		'bsca',
		'bscamaclbp',
		'phealth',
		'priority2',
	];
	return !hidden.includes(clientCode);
};

export const FOOTER_LANGUAGE_LINKS = [
	{
		lang: 'ar',
		dir: 'rtl',
		label: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#ar',
	},
	{
		lang: 'zh',
		dir: 'ltr',
		label: '\u7E41\u9AD4\u4E2D\u6587',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#ch',
	},
	{
		lang: 'en',
		dir: 'ltr',
		label: 'English',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx',
	},
	{
		lang: 'ium',
		dir: 'ltr',
		label: 'Mien',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#ium',
	},
	{
		lang: 'fr',
		dir: 'ltr',
		label: 'Fran\u00E7ais',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#fr',
	},
	{
		lang: 'nl',
		dir: 'ltr',
		label: 'Deutsch',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#ge',
	},
	{
		lang: 'hi',
		dir: 'ltr',
		label: '\u0939\u093F\u0902\u0926\u0940',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#hi',
	},
	{
		lang: 'it',
		dir: 'ltr',
		label: 'Italiano',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#it',
	},
	{
		lang: 'lo',
		dir: 'ltr',
		label: 'Laotian',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#lo',
	},
	{
		lang: 'ja',
		dir: 'ltr',
		label: '\u65E5\u672C\u8A9E',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#ja',
	},
	{
		lang: 'ko',
		dir: 'ltr',
		label: '\uD55C\uAD6D\uC5B4',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#ko',
	},
	{
		lang: 'ht',
		dir: 'ltr',
		label: 'Krey\u00F2l Ayisyen',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#kr',
	},
	{
		lang: 'fa',
		dir: 'rtl',
		label: '\u0641\u0627\u0631\u0633\u06CC',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#pe',
	},
	{
		lang: 'uk',
		dir: 'ltr',
		label: 'Ukranian',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#uk',
	},
	{
		lang: 'pl',
		dir: 'ltr',
		label: 'Polski',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#po',
	},
	{
		lang: 'pt',
		dir: 'ltr',
		label: 'Portugu\u00EAs',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#pt',
	},
	{
		lang: 'es',
		dir: 'ltr',
		label: 'Espa\u00F1ol',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#sp',
	},
	{
		lang: 'tl',
		dir: 'ltr',
		label: 'Tagalog',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#ta',
	},
	{
		lang: 'vi',
		dir: 'ltr',
		label: 'Ti\u1EBFng Vi\u1EC7t',
		href: '/ASH/public/Applications/NetworkSearch/LanguageServices.aspx#vi',
	},
];

// ---------------------------------------------------------------------------
// Error states
// ---------------------------------------------------------------------------

export const ERROR_CONTENT = {
	missingCookies: {
		heading: 'Find A Provider',
		body: 'Please visit your health plan\u2019s website to search for a participating provider or call ASH customer service at (800) 678-9133.',
		footnote:
			'If you feel that this page is appearing in error, please ensure that your browser has cookies enabled for this site before retrying.',
	},
	misalignedEntity: {
		heading: 'Find A Provider',
		body: 'We\u2019re sorry \u2014 there was an error displaying the search features for this health plan. Our technical team has been notified and is working to resolve the issue. If you would like more information, please call ASH customer service at (800) 678-9133.',
		footnote:
			'If you feel that this page is appearing in error, please ensure that your browser has cookies enabled for this site before retrying.',
	},
};
